import { DollarSign, ArrowRight, TrendingUp, TrendingDown } from "lucide-react";
import { Link } from "react-router-dom";
import { fmtCurrency } from "../../lib/format";

/**
 * <ValuationSnapshotCard/> — free-tier valuation headline.
 *
 * Renders a big, prominent "Your business is worth $X – $Y" card right after
 * the Snapshot on every report. Owners see the range at no cost; the full
 * lender/buyer breakdown and methodology stays behind Pro Pass in the
 * Insights section (via <ValuationRangeCard/>).
 *
 * When a prior period exists in local history that also had a valuation,
 * we render a dollar-change delta directly below the range — this is the
 * "quarter-over-quarter enterprise value" hook that ties valuation to
 * the returning-user experience.
 */
export default function ValuationSnapshotCard({
  valuation,
  priorValuation,
  proActive,
  onUnlock,
}) {
  if (!valuation) return null;

  // Negative-EBITDA / not-available case — one-liner so the section
  // exists but the range doesn't mislead.
  if (!valuation.available) {
    return (
      <div className="mt-10" data-testid="valuation-snapshot-card">
        <div className="bg-white border border-[#E5E0D8] rounded-2xl p-6 sm:p-8">
          <div className="flex items-center gap-2.5 mb-2">
            <DollarSign
              size={14}
              strokeWidth={1.5}
              className="text-[#C26D5C]"
            />
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#C26D5C] font-medium">
              Enterprise value
            </p>
          </div>
          <p className="font-serif-display text-xl text-[#1F382E] tracking-tight leading-tight">
            Not enough EBITDA to estimate value this period.
          </p>
          <p className="text-[13px] text-[#5C665D] leading-relaxed mt-2 max-w-xl">
            {valuation.reason ||
              "Rebuild profitability to see a valuation range next quarter."}
          </p>
        </div>
      </div>
    );
  }

  const cur = valuation.currency || "USD";
  const low = valuation.buyer_view?.low ?? 0;
  const high = valuation.buyer_view?.high ?? 0;
  const mid = valuation.buyer_view?.mid ?? Math.round((low + high) / 2);

  // Compute prior comparison from local history if we have one.
  const priorMid = priorValuation?.mid ?? null;
  const delta = priorMid != null ? mid - priorMid : null;
  const deltaTone =
    delta == null
      ? null
      : Math.abs(delta) < mid * 0.02
        ? "flat"
        : delta > 0
          ? "up"
          : "down";
  const DeltaIcon = deltaTone === "down" ? TrendingDown : TrendingUp;

  return (
    <div className="mt-10" data-testid="valuation-snapshot-card">
      <div className="bg-white border border-[#E5E0D8] rounded-2xl overflow-hidden">
        <div className="px-6 sm:px-8 py-6 sm:py-7">
          <div className="flex items-center gap-2.5">
            <DollarSign
              size={14}
              strokeWidth={1.5}
              className="text-[#C26D5C]"
            />
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#C26D5C] font-medium">
              Enterprise value
            </p>
          </div>
          <p className="font-serif-display text-xl text-[#1F382E] mt-1 tracking-tight">
            What your business is likely worth today.
          </p>

          <div
            className="mt-4 flex items-baseline gap-2 flex-wrap"
            data-testid="valuation-snapshot-range"
          >
            <span className="font-serif-display text-4xl sm:text-5xl text-[#1F382E] tabular-nums tracking-tight">
              {fmtCurrency(low, cur)}
            </span>
            <span className="text-[#C26D5C] text-3xl">—</span>
            <span className="font-serif-display text-4xl sm:text-5xl text-[#1F382E] tabular-nums tracking-tight">
              {fmtCurrency(high, cur)}
            </span>
          </div>

          {delta != null && deltaTone !== "flat" && (
            <div
              className="mt-4 flex items-center gap-2 flex-wrap"
              data-testid="valuation-snapshot-delta"
            >
              <span
                className={`inline-flex items-center gap-1.5 text-[13px] font-medium rounded-full px-3 py-1.5 ${
                  deltaTone === "up"
                    ? "text-[#2C5C43] bg-[#2C5C43]/10 border border-[#2C5C43]/20"
                    : "text-[#A84632] bg-[#A84632]/10 border border-[#A84632]/20"
                }`}
              >
                <DeltaIcon size={13} strokeWidth={2} />
                {deltaTone === "up" ? "+" : ""}
                {fmtCurrency(delta, cur)} vs your last period
              </span>
              <span className="text-[12px] text-[#5C665D]">
                (was {fmtCurrency(priorMid, cur)})
              </span>
            </div>
          )}
          {delta != null && deltaTone === "flat" && (
            <p
              className="mt-4 text-[13px] text-[#5C665D]"
              data-testid="valuation-snapshot-delta-flat"
            >
              Roughly unchanged vs last period ({fmtCurrency(priorMid, cur)}).
            </p>
          )}

          <p className="mt-5 text-[13px] text-[#5C665D] leading-relaxed max-w-xl">
            Based on your industry&apos;s typical EV/EBITDA multiple applied
            to this period&apos;s EBITDA and adjusted for your CFO Grade
            quality factor.
          </p>

          {!proActive && (
            <div
              className="mt-6 pt-6 border-t border-[#E5E0D8] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
              data-testid="valuation-snapshot-upsell"
            >
              <div className="max-w-md">
                <p className="text-[10.5px] uppercase tracking-[0.22em] text-[#9c6b1d] font-medium mb-1">
                  ★ In the paid report
                </p>
                <p className="text-[14px] text-[#1F382E] font-medium leading-snug">
                  Lender view vs buyer view · implied equity to owners ·
                  sensitivity · methodology
                </p>
                <p className="text-[12.5px] text-[#5C665D] leading-snug mt-1">
                  Also see what levers move this range fastest for a business
                  your size.
                </p>
              </div>
              {onUnlock ? (
                <button
                  onClick={onUnlock}
                  className="shrink-0 inline-flex items-center gap-1.5 bg-[#1F382E] hover:bg-[#142922] text-[#F7F5F0] rounded-md h-10 px-5 text-[13px] font-medium transition-all duration-300"
                  data-testid="valuation-snapshot-unlock-btn"
                >
                  Unlock full valuation
                  <ArrowRight size={13} strokeWidth={1.5} />
                </button>
              ) : (
                <Link
                  to="/analyze"
                  className="shrink-0 inline-flex items-center gap-1.5 bg-[#1F382E] hover:bg-[#142922] text-[#F7F5F0] rounded-md h-10 px-5 text-[13px] font-medium transition-all duration-300"
                >
                  Unlock full valuation
                  <ArrowRight size={13} strokeWidth={1.5} />
                </Link>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
